import { TimeSlot } from "@clipboard-health/contract-worker-app-bff";
import { z } from "zod";

import { API_TYPES } from "../api";

export const newsFeedItemCommentSchema = z.object({
  id: z.string(),
  workerId: z.string(),
  content: z.string(),
  createdAt: z.string(),
});
export type NewsFeedItemComment = z.infer<typeof newsFeedItemCommentSchema>;

export enum NewsFeedItemReactionEmoji {
  HEART_BUNNY = "HEART_BUNNY",
  MONEY_BUNNY = "MONEY_BUNNY",
  CHAMPAGNE_BUNNY = "CHAMPAGNE_BUNNY",
  AWARD_BUNNY = "AWARD_BUNNY",
}
export const NewsFeedItemReactionEmojiSchema = z.nativeEnum(NewsFeedItemReactionEmoji);

export const newsFeedItemReactionSchema = z.object({
  id: z.string(),
  workerId: z.string(),
  emojiType: NewsFeedItemReactionEmojiSchema,
});
export type NewsFeedItemReaction = z.infer<typeof newsFeedItemReactionSchema>;

export enum NewsFeedItemPostEvent {
  WORKER_BOOKED_SHIFT_AT_WORKPLACE_WITH_OPENINGS = "WORKER_BOOKED_SHIFT_AT_WORKPLACE_WITH_OPENINGS",
  WORKER_REVIEWED_WORKPLACE = "WORKER_REVIEWED_WORKPLACE",
  REFERRED_NEW_WORKER_JOINED = "REFERRED_NEW_WORKER_JOINED",
  WORKER_RECEIVED_INSTANT_PAY = "WORKER_RECEIVED_INSTANT_PAY",
}
export const newsFeedItemPostEventSchema = z.nativeEnum(NewsFeedItemPostEvent);

export enum NewsFeedItemRecommendationEvent {
  FRIEND_RECOMMENDATION = "FRIEND_RECOMMENDATION",
  WORKPLACE_RECOMMENDATION = "WORKPLACE_RECOMMENDATION",
}
export const newsFeedItemRecommendationEventSchema = z.nativeEnum(NewsFeedItemRecommendationEvent);

export enum NewsFeedItemType {
  POST = "POST",
  RECOMMENDATION = "RECOMMENDATION",
}
export const newsFeedItemTypeSchema = z.nativeEnum(NewsFeedItemType);

export const newsFeedItemPostMetadataSchema = z.object({
  event: z.nativeEnum(NewsFeedItemPostEvent),
  workplaceId: z.string().optional(),
  referredWorkerId: z.string().optional(),
  shiftId: z.string().optional(),
  workplaceCommentId: z.string().optional(),
});
export type NewsFeedItemPostMetadata = z.infer<typeof newsFeedItemPostMetadataSchema>;

export const newsFeedItemPostAttributesSchema = z.object({
  type: z.literal(NewsFeedItemType.POST),
  createdAt: z.string(),
  createdBy: z.string(),
  metadata: newsFeedItemPostMetadataSchema,
  comments: z.array(newsFeedItemCommentSchema),
  reactions: z.array(newsFeedItemReactionSchema),
});
export type NewsFeedItemPostAttributes = z.infer<typeof newsFeedItemPostAttributesSchema>;

export const newsFeedItemRecommendationMetadataSchema = z.object({
  event: z.nativeEnum(NewsFeedItemRecommendationEvent),
  workplaceId: z.string().optional(),
  recommendedWorkplaceId: z.string().optional(),
  recommendedWorkerId: z.string().optional(),
});
export type NewsFeedItemRecommendationMetadata = z.infer<
  typeof newsFeedItemRecommendationMetadataSchema
>;

export const newsFeedItemRecommendationAttributesSchema = z.object({
  type: z.literal(NewsFeedItemType.RECOMMENDATION),
  createdAt: z.string(),
  recommendedBy: z.string(),
  recommendedFor: z.string(),
  metadata: newsFeedItemRecommendationMetadataSchema,
});
export type NewsFeedItemRecommendationAttributes = z.infer<
  typeof newsFeedItemRecommendationAttributesSchema
>;

export const newsFeedItemSchema = z.object({
  type: z.literal(API_TYPES.newsFeedItem),
  id: z.string(),
  attributes: z.discriminatedUnion("type", [
    newsFeedItemPostAttributesSchema,
    newsFeedItemRecommendationAttributesSchema,
  ]),
});
export type NewsFeedItem = z.infer<typeof newsFeedItemSchema>;

export const newsFeedItemIncludedShiftSchema = z.object({
  type: z.literal("shift"),
  id: z.string(),
  attributes: z.object({
    start: z.string(),
    end: z.string(),
    qualification: z.string(),
    time: z.number(),
    name: z.nativeEnum(TimeSlot),
  }),
});
export type NewsFeedItemIncludedShift = z.infer<typeof newsFeedItemIncludedShiftSchema>;

export const newsFeedItemIncludedWorkplaceSchema = z.object({
  type: z.literal("workplace"),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
  }),
});
export type NewsFeedItemIncludedWorkplace = z.infer<typeof newsFeedItemIncludedWorkplaceSchema>;

export const newsFeedItemIncludedWorkerSchema = z.object({
  type: z.literal("worker"),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    qualification: z.string(),
  }),
});
export type NewsFeedItemIncludedWorker = z.infer<typeof newsFeedItemIncludedWorkerSchema>;

export const newsFeedItemIncludedSchema = z
  .array(
    z.discriminatedUnion("type", [
      newsFeedItemIncludedShiftSchema,
      newsFeedItemIncludedWorkplaceSchema,
      newsFeedItemIncludedWorkerSchema,
    ])
  )
  .optional();
export type NewsFeedItemIncluded = z.infer<typeof newsFeedItemIncludedSchema>;
